import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router-dom'
import {
  useContacts,
  useBulkDeleteContacts,
  useBulkUpdateContacts,
  useBulkAssignContacts,
  useAccount,
  useUsers,
} from '@/hooks/use-api'
import { ownerPickerOptions } from '@/lib/salesReps'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshButton } from '@/components/RefreshButton'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { ResizableTable } from '@/components/ui/resizable-table'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Users, Plus, Mail, Phone as PhoneIcon, Download } from 'lucide-react'
import type { Contact as _Contact } from '@/types'
import { exportContactsToCSV } from '@/lib/csv-export'
import { CreateContactModal } from '@/components/CreateContactModal'
import { BulkActionsBar } from '@/components/BulkActionsBar'
import { BulkDeleteDialog } from '@/components/BulkDeleteDialog'
import { BulkUpdateModal, type BulkUpdateField } from '@/components/BulkUpdateModal'
import { BulkAssignModal } from '@/components/BulkAssignModal'
import { MergeRecordsModal } from '@/components/MergeRecordsModal'
import {
  createContactsColumns,
  contactsDefaultColumnSizing,
} from '@/config/columns/contacts-columns'
import { ColumnPicker } from '@/components/ui/column-picker'
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { useColumnVisibility } from '@/hooks/use-column-visibility'
import { useToast } from '@/hooks/use-toast'
import { useReadOnly } from '@/hooks/use-read-only'
import { useUser } from '@/contexts/UserContext'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { MobileContactList } from '@/components/MobileContactCard'
import { useMobile } from '@/hooks/use-mobile'
import { explainError } from '@/lib/explain-error'

export default function Contacts() {
  const { currentUser, isAdmin, impersonatedUser } = useUser()
  const fk = makeFilterKey(currentUser?.id, 'contacts')
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [sortBy, setSortBy] = usePersistentState(fk('sortBy'), 'name')
  const [sortOrder, setSortOrder] = usePersistentState<'asc' | 'desc'>(fk('sortOrder'), 'asc')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false)
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false)
  const [mergeModalOpen, setMergeModalOpen] = useState(false)
  const isMobile = useMobile()

  // Read account filter from URL params
  const [searchParams, setSearchParams] = useSearchParams()
  const accountIdFilter = searchParams.get('accountId') || undefined

  // Default to "All Contacts" when filtering by account/entity. Persist the
  // toggle only when NOT account-scoped (an ?accountId visit should show that
  // account's contacts regardless of a previously-remembered My/All choice).
  const [showMyContacts, setShowMyContacts] = usePersistentState(
    accountIdFilter ? undefined : fk('showMy'),
    !isAdmin && !accountIdFilter
  )

  // Owner scope from a dashboard deep-link (?owner_id=… / ?my). Overrides the
  // My/All toggle on arrival so the list matches the card; cleared on toggle.
  const [deepLinkOwnerId, setDeepLinkOwnerId] = useState<string | undefined>(undefined)

  // Active/All/Inactive filter (from Contact_Extensions.status__c). Default to
  // Active so the 43 inactive contacts (people who've left or retired) don't
  // show up in the default view. Persisted in URL params for shareable links.
  const statusParam = searchParams.get('status')
  const initialStatusFilter: 'Active' | 'All' | 'Inactive' =
    statusParam === 'All' || statusParam === 'Inactive' ? statusParam : 'Active'
  const [statusFilter, setStatusFilterState] = useState<'Active' | 'All' | 'Inactive'>(
    initialStatusFilter
  )
  // Marketing Status multiselect filter (empty = no filter; AND with the others).
  const [marketingStatuses, setMarketingStatuses] = useState<string[]>([])
  const marketingStatusOptions = usePicklistOptions('contact', 'marketing_status__c')
  // Lead Source multiselect filter (empty = no filter; AND with the others).
  const [leadSources, setLeadSources] = useState<string[]>([])
  const leadSourceOptions = usePicklistOptions('contact', 'lead_source')
  // Column-level filters (empty = no filter).
  const [ownerIds, setOwnerIds] = useState<string[]>([])
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const setStatusFilter = useCallback(
    (next: 'Active' | 'All' | 'Inactive') => {
      setStatusFilterState(next)
      // Reflect in URL so the view is shareable. Default 'Active' = no param.
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next === 'Active') {
            params.delete('status')
          } else {
            params.set('status', next)
          }
          return params
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  // Apply the dashboard deep-link once: scope to the card's owner and show all
  // statuses so the list matches the dashboard's all-contacts count (the list
  // otherwise defaults to "Active"). Skipped when account-scoped (?accountId).
  const dashboardAppliedRef = useRef(false)
  useEffect(() => {
    if (dashboardAppliedRef.current) return
    if (accountIdFilter) return
    if (!searchParams.has('owner_id') && !searchParams.has('my')) return
    dashboardAppliedRef.current = true
    setStatusFilter('All')
    const me = impersonatedUser?.id || currentUser?.id
    const ownerId = searchParams.get('owner_id') || (searchParams.has('my') ? me : undefined)
    setDeepLinkOwnerId(ownerId)
    setShowMyContacts(!!ownerId && ownerId === me)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Only Admin/Super Admin can export (and not when impersonating a non-admin)
  const canExport =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  const { data, isLoading } = useContacts({
    search,
    page,
    account_id: accountIdFilter,
    owner_id:
      deepLinkOwnerId ?? (showMyContacts ? impersonatedUser?.id || currentUser?.id : undefined),
    status__c: statusFilter === 'All' ? undefined : statusFilter,
    marketing_status__c: marketingStatuses.length ? marketingStatuses.join(',') : undefined,
    lead_source: leadSources.length ? leadSources.join(',') : undefined,
    owner_ids: ownerIds.length ? ownerIds.join(',') : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  })

  const handleSort = useCallback(
    (field: string) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(field)
        setSortOrder('asc')
      }
      firstPage()
    },
    [sortBy, firstPage]
  )
  const { data: filterAccount } = useAccount(accountIdFilter || '')
  const bulkDeleteMutation = useBulkDeleteContacts()
  const bulkUpdateMutation = useBulkUpdateContacts()
  const bulkAssignMutation = useBulkAssignContacts()
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = usersData?.data ?? []
  const { toast } = useToast()
  const isReadOnly = useReadOnly()

  const {
    items: contacts,
    total,
    currentPage,
    limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
  } = extract(data)

  // Handle selection
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleToggleSelectAll = () => {
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(contacts.map((contact) => contact.id)))
    }
  }

  const handleClearSelection = () => {
    setSelectedIds(new Set())
  }

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      const idsArray = Array.from(selectedIds)
      await bulkDeleteMutation.mutateAsync(idsArray)

      toast({
        title: 'Success',
        description: `${idsArray.length} contact${idsArray.length === 1 ? '' : 's'} deleted successfully.`,
      })

      setBulkDeleteDialogOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete contacts')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk update
  const handleBulkUpdate = async (updates: Record<string, any>) => {
    try {
      await bulkUpdateMutation.mutateAsync({ ids: Array.from(selectedIds), updates })

      toast({
        title: 'Success',
        description: `${selectedIds.size} contact${selectedIds.size === 1 ? '' : 's'} updated successfully.`,
      })

      setBulkUpdateModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update contacts')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk assign
  const handleBulkAssign = async (ownerId: string, notifyOwner: boolean) => {
    try {
      await bulkAssignMutation.mutateAsync({
        ids: Array.from(selectedIds),
        owner_id: ownerId,
        notify_owner: notifyOwner,
      })

      toast({
        title: 'Success',
        description: `${selectedIds.size} contact${selectedIds.size === 1 ? '' : 's'} assigned successfully.`,
      })

      setBulkAssignModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to assign contacts')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle CSV export
  const handleExportCSV = () => {
    // For now, export the current page of contacts
    // In a production app, you might want to fetch all contacts from the API
    exportContactsToCSV(contacts)
    toast({
      title: 'Export Successful',
      description: `Exported ${contacts.length} contacts to CSV`,
    })
  }

  // Define field configurations
  const contactsUpdateFields: BulkUpdateField[] = [
    { key: 'title', label: 'Title', type: 'text', placeholder: 'Enter title' },
    { key: 'department', label: 'Department', type: 'text', placeholder: 'Enter department' },
    { key: 'lead_source', label: 'Lead Source', type: 'text', placeholder: 'Enter lead source' },
  ]

  // Create columns with callbacks
  const columns = useMemo(
    () =>
      createContactsColumns({
        selectedIds,
        onToggleSelect: handleToggleSelect,
        onToggleSelectAll: handleToggleSelectAll,
        allSelected: selectedIds.size === contacts.length && contacts.length > 0,
      }),
    [selectedIds, contacts.length]
  )

  const [columnVisibility, setColumnVisibility] = useColumnVisibility(
    'contacts-column-visibility',
    { marketing_status__c: false }
  )

  return (
    <div className="space-y-4">
      {selectedIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          entityType="contact"
          onClearSelection={handleClearSelection}
          onBulkDelete={() => setBulkDeleteDialogOpen(true)}
          onBulkUpdate={() => setBulkUpdateModalOpen(true)}
          onBulkAssign={() => setBulkAssignModalOpen(true)}
          onMerge={() => setMergeModalOpen(true)}
          isDeleting={bulkDeleteMutation.isPending}
          isUpdating={bulkUpdateMutation.isPending}
          isAssigning={bulkAssignMutation.isPending}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="contact" />
            <h1 className="text-3xl font-bold tracking-tight">Contacts</h1>
          </div>
          <p className="text-muted-foreground mt-1">Manage your contacts and relationships</p>
          {accountIdFilter && filterAccount && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
                Filtered by:{' '}
                <Link to={`/accounts/${filterAccount.id}`} className="font-medium hover:underline">
                  {filterAccount.name}
                </Link>
              </span>
              <Link to="/contacts" className="text-sm text-muted-foreground hover:text-foreground">
                Clear filter
              </Link>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <RefreshButton queryKey={['contacts']} label="contacts" />
          {canExport && (
            <Button variant="outline" aria-label="Export contacts to CSV" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          )}
          <Button
            aria-label="Create new contact"
            onClick={() => setCreateModalOpen(true)}
            disabled={isReadOnly}
            title={isReadOnly ? 'Cannot create contacts while viewing as another user' : undefined}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New Contact
          </Button>
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Contacts</CardTitle>
            <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{total}</div>
            <p className="text-xs text-muted-foreground leading-tight">
              Showing {contacts.length} on this page
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">With Email</CardTitle>
            <Mail className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{data?.stats?.withEmail ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">With Phone</CardTitle>
            <PhoneIcon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{data?.stats?.withPhone ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{showMyContacts ? 'My Contacts' : 'All Contacts'}</CardTitle>
          <CardDescription>View and manage your contacts</CardDescription>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Tabs
              defaultValue="all"
              value={showMyContacts ? 'my' : 'all'}
              onValueChange={(value) => {
                setDeepLinkOwnerId(undefined)
                setShowMyContacts(value === 'my')
                firstPage()
              }}
            >
              <TabsList aria-label="Filter by ownership">
                <TabsTrigger value="all" aria-label="Show all contacts">
                  All Contacts
                </TabsTrigger>
                <TabsTrigger value="my" aria-label="Show only my contacts">
                  My Contacts
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Tabs
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as 'Active' | 'All' | 'Inactive')
                firstPage()
              }}
            >
              <TabsList aria-label="Filter by status">
                <TabsTrigger value="Active" aria-label="Show only active contacts">
                  Active
                </TabsTrigger>
                <TabsTrigger value="All" aria-label="Show active and inactive contacts">
                  All
                </TabsTrigger>
                <TabsTrigger value="Inactive" aria-label="Show only inactive contacts">
                  Inactive
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <SearchableMultiSelect
              label="Marketing Status"
              options={marketingStatusOptions.map((v) => ({ value: v, label: v }))}
              selected={marketingStatuses}
              onChange={(next) => {
                setMarketingStatuses(next)
                firstPage()
              }}
              searchPlaceholder="Filter marketing status..."
            />
            <SearchableMultiSelect
              label="Lead Source"
              options={leadSourceOptions.map((v) => ({ value: v, label: v }))}
              selected={leadSources}
              onChange={(next) => {
                setLeadSources(next)
                firstPage()
              }}
              searchPlaceholder="Filter lead source..."
            />
            <SearchableMultiSelect
              label="Owner"
              options={ownerPickerOptions(users).map((u) => ({ value: u.id, label: u.name }))}
              selected={ownerIds}
              onChange={(next) => {
                setOwnerIds(next)
                firstPage()
              }}
              searchPlaceholder="Filter owner..."
            />
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="contact-date-from" className="text-xs text-muted-foreground">
                  Added from
                </Label>
                <Input
                  id="contact-date-from"
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => {
                    setDateFrom(e.target.value)
                    firstPage()
                  }}
                  className="h-9 w-[9.5rem]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="contact-date-to" className="text-xs text-muted-foreground">
                  Added to
                </Label>
                <Input
                  id="contact-date-to"
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => {
                    setDateTo(e.target.value)
                    firstPage()
                  }}
                  className="h-9 w-[9.5rem]"
                />
              </div>
            </div>
            <SearchWithRecent
              value={search}
              onChange={setSearch}
              placeholder="Search contacts..."
              ariaLabel="Search contacts"
              entityKey="contacts"
            />
            <ColumnPicker
              columns={columns}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading contacts...</div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No contacts found. Create your first contact to get started.
            </div>
          ) : isMobile ? (
            <MobileContactList
              contacts={contacts}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
            />
          ) : (
            <ResizableTable
              columns={columns}
              data={contacts}
              storageKey="contacts-column-widths"
              defaultColumnSizing={contactsDefaultColumnSizing}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          )}

          {/* Pagination */}
          {!isLoading && total > limit && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of{' '}
                {total} contacts
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrevPage}>
                  Previous
                </Button>
                <div className="flex items-center gap-1 text-sm">
                  Page {currentPage} of {totalPages}
                </div>
                <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNextPage}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateContactModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        accountId={accountIdFilter}
        ownerId={filterAccount?.owner_id}
        onCreated={(contact) => navigate(`/contacts/${contact.id}`)}
      />

      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        selectedCount={selectedIds.size}
        entityType="contact"
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleteMutation.isPending}
      />

      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onOpenChange={setBulkUpdateModalOpen}
        selectedCount={selectedIds.size}
        entityType="contact"
        fields={contactsUpdateFields}
        onConfirm={handleBulkUpdate}
        isUpdating={bulkUpdateMutation.isPending}
      />

      <BulkAssignModal
        open={bulkAssignModalOpen}
        onOpenChange={setBulkAssignModalOpen}
        selectedCount={selectedIds.size}
        entityType="contact"
        onConfirm={handleBulkAssign}
        isAssigning={bulkAssignMutation.isPending}
      />

      {selectedIds.size === 2 &&
        (() => {
          const [idA, idB] = Array.from(selectedIds) as [string, string]
          return (
            <MergeRecordsModal
              entityType="Contact"
              recordIdA={idA}
              recordIdB={idB}
              open={mergeModalOpen}
              onOpenChange={(open) => {
                setMergeModalOpen(open)
                if (!open) setSelectedIds(new Set())
              }}
            />
          )
        })()}
    </div>
  )
}
